import { CategoryToggles } from './CategoryToggles';
import { ModeSelector } from './ModeSelector';
import { DetectionList } from './DetectionList';
import { StatusBanner } from './StatusBanner';
import type {
  ScanResponse,
  EntityType,
  AppStatus,
  AnonymizationMode,
} from '../types/api';
import { getAnonymizedFilename } from '../utils/filename';
import { MODE_VERB } from '../utils/modes';

interface Props {
  scan: ScanResponse;
  disabled: Set<EntityType>;
  selectedIndices: Set<number>;
  mode: AnonymizationMode;
  maskChar: string;
  onModeChange: (mode: AnonymizationMode) => void;
  onMaskCharChange: (char: string) => void;
  onToggleType: (type: EntityType, enabled: boolean) => void;
  onToggleDetection: (index: number, selected: boolean) => void;
  onPseudonymize: () => void;
  onReset: () => void;
  pseudonymizing: boolean;
  status: AppStatus | null;
  filename: string;
}

export function ReviewStep({
  scan,
  disabled,
  selectedIndices,
  mode,
  maskChar,
  onModeChange,
  onMaskCharChange,
  onToggleType,
  onToggleDetection,
  onPseudonymize,
  onReset,
  pseudonymizing,
  status,
  filename,
}: Props) {
  const total = scan.detections.length;
  const enabledCount = selectedIndices.size;
  const hasFindings = total > 0;
  const allDisabled = enabledCount === 0;
  const anonymizedFilename = getAnonymizedFilename(filename);
  const verb = MODE_VERB[mode];

  const headerContent = (() => {
    if (!hasFindings) {
      return (
        <StatusBanner status={{ kind: 'success', message: '' }}>
          Nenhum dado pessoal detectado. O arquivo está limpo.
        </StatusBanner>
      );
    }
    if (status) {
      return (
        <StatusBanner
          status={status}
          showSpinner={status.kind === 'info' && pseudonymizing}
        />
      );
    }
    return (
      <StatusBanner status={{ kind: 'info', message: '' }}>
        Encontrado{total === 1 ? '' : 's'} <strong>{total}</strong> ocorrência{total === 1 ? '' : 's'} de dados pessoais.
        Escolha como substituí-{total === 1 ? 'la' : 'las'} e revise as categorias abaixo.
      </StatusBanner>
    );
  })();

  return (
    <div className="card">
      <h2 className="card-title">Revisar e Processar</h2>

      <div className="file-info">
        <div className="file-info-row">
          <span className="file-info-label">Arquivo original:</span>
          <span className="file-info-value">{filename}</span>
        </div>
        <div className="file-info-row">
          <span className="file-info-label">Arquivo anonimizado:</span>
          <span className="file-info-value file-info-output">{anonymizedFilename}</span>
        </div>
      </div>

      {headerContent}

      {hasFindings && (
        <>
          <ModeSelector
            mode={mode}
            maskChar={maskChar}
            onModeChange={onModeChange}
            onMaskCharChange={onMaskCharChange}
            disabled={pseudonymizing}
          />
          <CategoryToggles
            summary={scan.summary}
            disabled={disabled}
            onToggle={onToggleType}
          />
          <DetectionList
            detections={scan.detections}
            selectedIndices={selectedIndices}
            onToggle={onToggleDetection}
          />
        </>
      )}

      <div className="actions">
        {hasFindings && (
          <span className="info-text">
            {enabledCount === total
              ? `${enabledCount} ocorrências serão ${verb}`
              : `${enabledCount} de ${total} ocorrências serão ${verb}`}
          </span>
        )}
        <button className="btn" onClick={onReset}>Recomeçar</button>
        <button
          className="btn btn-primary"
          disabled={!hasFindings || allDisabled || pseudonymizing}
          onClick={onPseudonymize}
        >
          {pseudonymizing
            ? <><span className="spinner" />Anonimizando…</>
            : 'Anonimizar e Baixar'}
        </button>
      </div>
    </div>
  );
}
